import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NotificationService from '../services/notificationService';

/**
 * Hook for fetching notifications with infinite scroll pagination
 * @param {Object} options - Query options
 * @param {string} options.leagueSlug - Filter by league slug (null or 'all' for all leagues)
 * @param {boolean} options.read - Filter by read status (undefined for all, false for unread only)
 * @param {string} options.type - Filter by notification type (undefined or 'all' for all types)
 * @param {boolean} options.enabled - Whether the query is enabled
 */
export const useGetNotifications = (options = {}) => {
  const { leagueSlug, read, type, enabled = true } = options;

  return useInfiniteQuery({
    queryKey: ['notifications', leagueSlug, read, type],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await NotificationService.getNotifications({
        limit: 20,
        offset: pageParam,
        read,
        type: type !== 'all' ? type : undefined,
        leagueSlug: leagueSlug !== 'all' ? leagueSlug : undefined
      });
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((acc, page) =>
        acc + page.notifications.length, 0);
      if (lastPage.pagination && totalFetched < lastPage.pagination.total) {
        return totalFetched;
      }
      return undefined;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook for fetching unread notification count
 * @param {string} leagueSlug - Filter by league slug (null for all leagues)
 * @param {boolean} enabled - Whether the query is enabled
 */
export const useGetUnreadCount = (leagueSlug = null, enabled = true) => {
  return useQuery({
    queryKey: ['notifications-unread-count', leagueSlug],
    queryFn: async () => {
      const response = await NotificationService.getUnreadCount(leagueSlug);
      return response;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook for marking a single notification as read
 */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['mark-notification-read'],
    mutationFn: async (notificationId) => {
      const response = await NotificationService.markAsRead(notificationId);
      return response;
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

/**
 * Hook for marking all notifications as read
 */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['mark-all-notifications-read'],
    mutationFn: async (leagueSlug = null) => {
      const response = await NotificationService.markAllAsRead(leagueSlug);
      return response;
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

/**
 * Hook for clearing all notifications
 */
export const useClearAllNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['clear-all-notifications'],
    mutationFn: async (leagueSlug = null) => {
      const response = await NotificationService.clearAll(leagueSlug);
      return response;
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};

/**
 * Hook for deleting a single notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['delete-notification'],
    mutationFn: async (notificationId) => {
      const response = await NotificationService.deleteNotification(notificationId);
      return response;
    },
    onSuccess: () => {
      // Invalidate notifications queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
};
